import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { EditPencil, Check, Xmark, Book, Cart, NavArrowDown, NavArrowUp, Search, Sparks, Settings, Star, Calendar, Plus, Trash } from "iconoir-react";
import { Link } from "react-router-dom";
import {
    fetchWeek,
    setMeal,
    firstName,
    fetchRecipes,
    fetchHousehold,
    updateHouseholdSettings,
    addMealToShopping,
    addWeekToShopping,
    fixMyMenu,
    saveMealToVault,
    updateMember,
    pickOneRecipe,
    backfillMissingMealContent,
} from "@/lib/api";
import { avatarColor, initials } from "@/pages/MiloApp";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function fmtDate(iso, locale) {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(locale, { month: "short", day: "numeric" });
}
function fmtWeekRange(weekStartIso, locale) {
    const start = new Date(weekStartIso + "T00:00:00");
    const end = new Date(start); end.setDate(end.getDate() + 6);
    const sm = start.toLocaleDateString(locale, { month: "short", day: "numeric" });
    const em = end.toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" });
    return `${sm} – ${em}`;
}
function dateForDayInWeek(weekStartIso, dayName) {
    const idx = DAYS.indexOf(dayName);
    const d = new Date(weekStartIso + "T00:00:00");
    d.setDate(d.getDate() + idx);
    return d.toISOString().slice(0, 10);
}

export default function WeekTab({ session, members, onRefresh }) {
    const { t, lang } = useT();
    const locale = lang === "fr" ? "fr-FR" : "en-US";
    const [weeks, setWeeks] = useState([]);
    const [editing, setEditing] = useState(null); // { weekStart, day }
    const [recipes, setRecipes] = useState([]);
    const [defaultServings, setDefaultServings] = useState(4);
    const [pickerFor, setPickerFor] = useState(null); // { onPick }
    const [addingWeek, setAddingWeek] = useState(null); // week_start string while in-flight
    const [isAdmin, setIsAdmin] = useState(false);
    const [menuSettings, setMenuSettings] = useState(null);
    const [fixingWeek, setFixingWeek] = useState(null); // week_start in-flight
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [overwrite, setOverwrite] = useState(false);

    const load = async () => {
        const [w, rs, hh] = await Promise.all([
            fetchWeek(session.household_id, 2),
            fetchRecipes(session.household_id).catch(() => []),
            fetchHousehold(session.household_id).catch(() => null),
        ]);
        setWeeks(w.weeks || []);
        setRecipes(rs || []);
        if (hh?.household?.default_servings) setDefaultServings(hh.household.default_servings);
        setIsAdmin(!!hh?.is_admin);
        setMenuSettings(hh?.household?.menu_settings || null);
        // Heal any meals that are missing ingredients/steps OR whose URL-bearing previews
        // were LLM-guessed before the URL-aware scraping fix shipped (m.scraped !== true).
        // Result will show up on next refresh — we re-fetch silently if anything was patched.
        const weeksData = w.weeks || [];
        const needsHeal = weeksData.some((wk) =>
            DAYS.some((d) => {
                const m = wk.days?.[d];
                if (!m || !m.meal_name) return false;
                if (m.recipe_id) return false;  // vault meals heal via recipe doc
                if (!(m.steps_preview || []).length || !(m.ingredients_preview || []).length) return true;
                if ((m.recipe_url || "").trim() && !m.scraped) return true;
                return false;
            })
        );
        if (needsHeal && weeksData[0]?.week_start) {
            (async () => {
                try {
                    const res = await backfillMissingMealContent(session.household_id, weeksData[0].week_start);
                    if (res?.patched_meals || res?.patched_recipes) {
                        const w2 = await fetchWeek(session.household_id, 2);
                        setWeeks(w2.weeks || []);
                        const rs2 = await fetchRecipes(session.household_id).catch(() => []);
                        setRecipes(rs2 || []);
                    }
                } catch (_) {
                    // silent — backfill is best-effort
                }
            })();
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.household_id]);

    const memberOf = (mid) => members?.find((m) => m.id === mid);
    const recipeById = useMemo(() => {
        const map = {};
        recipes.forEach((r) => { map[r.id] = r; });
        return map;
    }, [recipes]);

    const todayIso = new Date().toISOString().slice(0, 10);
    const startEdit = (weekStart, day) => setEditing({ weekStart, day });

    const save = async (payload) => {
        await setMeal(session.household_id, payload);
        setEditing(null);
        await load();
        onRefresh?.();
        toast.success(t("week.saved", { day: payload.day_of_week }));
    };

    const onChangeDefaultServings = async (val) => {
        const n = parseInt(val, 10);
        if (!n || n < 1 || n > 50) return;
        setDefaultServings(n);
        try {
            await updateHouseholdSettings(session.household_id, { default_servings: n });
            toast.success(t("week.hh_servings_saved"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const onAddMeal = async (meal_id) => {
        try {
            const res = await addMealToShopping(session.household_id, meal_id);
            toast.success(t("week.added_to_shopping", { added: res.added, merged: res.merged }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const onSaveMealToVault = async (meal_id) => {
        try {
            const res = await saveMealToVault(session.household_id, meal_id);
            if (res.already_saved) {
                toast.message(t("week.save_to_vault_already"));
            } else {
                toast.success(t("week.save_to_vault_saved"));
            }
            await load();
            onRefresh?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const onAddWeek = async (week_start) => {
        setAddingWeek(week_start);
        try {
            const res = await addWeekToShopping(session.household_id, week_start);
            toast.success(t("week.week_added_to_shopping", { added: res.added, merged: res.merged }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setAddingWeek(null);
        }
    };

    const onFixMenu = async (week_start, forceOverwrite = false) => {
        if (!isAdmin) {
            toast.error(t("week.fix_admin_only"));
            return;
        }
        setFixingWeek(week_start);
        try {
            const res = await fixMyMenu(session.household_id, {
                week_start,
                overwrite: forceOverwrite || overwrite,
            });
            if (res.filled === 0 && res.reason === "all_days_already_set") {
                // All 7 days already set — offer to reshuffle in one tap
                toast(t("week.fix_reshuffle_prompt"), {
                    action: {
                        label: t("week.fix_reshuffle_now"),
                        onClick: () => onFixMenu(week_start, true),
                    },
                    duration: 8000,
                });
            } else if (res.filled === 0) {
                toast.message(t("week.fix_none"));
            } else {
                toast.success(t("week.fix_done", { vault: res.from_vault, web: res.from_web }));
            }
            await load();
            onRefresh?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setFixingWeek(null);
        }
    };

    const saveMenuSettings = async (ms) => {
        try {
            const res = await updateHouseholdSettings(session.household_id, { menu_settings: ms });
            setMenuSettings(res?.household?.menu_settings || ms);
            toast.success(t("week.fix_settings_saved"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const onSaveMember = async (member_id, patch) => {
        try {
            await updateMember(member_id, patch);
            onRefresh?.();
            toast.success(t("week.fix_settings_saved"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    if (!weeks.length) return <div className="text-sm text-zinc-500">{t("week.loading")}</div>;

    return (
        <div data-testid="week-tab" className="space-y-6">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs font-semibold text-[#5b6d63] uppercase tracking-widest">{t("week.eyebrow")}</div>
                    <div className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight mt-1">{t("week.title")}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={() => setSettingsOpen(true)}
                        className="brut-btn brut-btn-secondary text-xs sm:text-sm whitespace-nowrap"
                        data-testid="open-menu-settings-btn"
                        title={t("week.hh_settings_title")}
                    >
                        <Settings className="w-4 h-4" />
                    </button>
                    <Link
                        data-testid="open-recipes-btn"
                        to="/recipes"
                        className="brut-btn brut-btn-secondary text-xs sm:text-sm whitespace-nowrap shrink-0"
                    >
                        <Book className="w-4 h-4" /> {t("recipes.book_button")}
                    </Link>
                </div>
            </div>

            {weeks.map((week, idx) => {
                const hasIngMeals = DAYS.some((d) => {
                    const m = week.days[d];
                    if (!m) return false;
                    if (m.recipe_id && recipeById[m.recipe_id]?.ingredients?.length) return true;
                    if ((m.ingredients_preview || []).length) return true;
                    return false;
                });
                const hasAnyMeal = DAYS.some((d) => !!week.days[d]);
                return (
                    <div key={week.week_start}>
                        <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                            <div>
                                <div className="text-[10.5px] font-bold text-[#2F6B43] uppercase tracking-widest">
                                    {idx === 0 ? t("week.this_week") : t("week.next_week")}
                                </div>
                                <div className="font-display text-2xl font-bold tracking-tight leading-none mt-0.5">
                                    {fmtWeekRange(week.week_start, locale)}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {isAdmin && (
                                    <button
                                        onClick={() => onFixMenu(week.week_start)}
                                        disabled={fixingWeek === week.week_start}
                                        className="brut-btn brut-btn-secondary text-[11px] sm:text-xs whitespace-nowrap"
                                        data-testid={`fix-menu-${week.week_start}`}
                                    >
                                        <Calendar className="w-3.5 h-3.5" />
                                        {fixingWeek === week.week_start ? t("week.fix_btn_working") : t("week.fix_btn")}
                                    </button>
                                )}
                                {hasIngMeals && (
                                    <button
                                        onClick={() => onAddWeek(week.week_start)}
                                        disabled={addingWeek === week.week_start}
                                        className="brut-btn brut-btn-secondary text-[11px] sm:text-xs whitespace-nowrap"
                                        data-testid={`week-add-to-shopping-${week.week_start}`}
                                    >
                                        <Cart className="w-3.5 h-3.5" /> {t("week.add_week_to_shopping")}
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {DAYS.map((d) => {
                                const meal = week.days[d];
                                const dateIso = dateForDayInWeek(week.week_start, d);
                                const isToday = dateIso === todayIso;
                                const isEditing = editing && editing.weekStart === week.week_start && editing.day === d;
                                const dayShort = new Date(dateIso + "T00:00:00").toLocaleDateString(locale, { weekday: "short" });
                                const linkedRecipe = meal?.recipe_id ? recipeById[meal.recipe_id] : null;
                                return (
                                    <div key={d} className={`brut-card p-4 sm:p-5 ${isToday ? "ring-2 ring-[#2F6B43]/30" : ""}`} data-testid={`day-card-${d}`}>
                                        <div className="flex items-center justify-between mb-1.5">
                                            <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#5b6d63]">
                                                {dayShort} · {fmtDate(dateIso, locale)}
                                                {isToday && <span className="ml-1 text-[#2F6B43]">· {t("week.today_marker")}</span>}
                                            </div>
                                            {!isEditing && (
                                                <button
                                                    data-testid={`edit-${week.week_start}-${d}-btn`}
                                                    onClick={() => startEdit(week.week_start, d)}
                                                    className="w-7 h-7 rounded-full bg-[#faf5ea] hover:bg-[#e8f1e4] flex items-center justify-center"
                                                >
                                                    <EditPencil className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                        {isEditing ? (
                                            <MealEditForm
                                                initial={meal}
                                                members={members}
                                                recipes={recipes}
                                                defaultServings={defaultServings}
                                                householdId={session.household_id}
                                                t={t}
                                                onPickRecipe={(pick) => setPickerFor({ pick })}
                                                onCancel={() => setEditing(null)}
                                                onSave={(payload) => save({ ...payload, day_of_week: d, week_start: week.week_start })}
                                            />
                                        ) : meal ? (
                                            <MealView
                                                meal={meal}
                                                linkedRecipe={linkedRecipe}
                                                memberOf={memberOf}
                                                defaultServings={defaultServings}
                                                onAddToShopping={() => onAddMeal(meal.id)}
                                                onSaveToVault={() => onSaveMealToVault(meal.id)}
                                                t={t}
                                            />
                                        ) : (
                                            <button
                                                onClick={() => startEdit(week.week_start, d)}
                                                className="text-sm text-[#5b6d63] italic hover:text-[#2F6B43] mt-1"
                                            >
                                                {t("week.add_meal")}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            <AnimatePresence>
                {pickerFor && (
                    <RecipePickerModal
                        recipes={recipes}
                        t={t}
                        onPick={(r) => { pickerFor.pick(r); setPickerFor(null); }}
                        onClose={() => setPickerFor(null)}
                    />
                )}
                {settingsOpen && (
                    <MenuSettingsModal
                        initial={menuSettings}
                        defaultServings={defaultServings}
                        members={members}
                        isAdmin={isAdmin}
                        overwrite={overwrite}
                        setOverwrite={setOverwrite}
                        t={t}
                        onChangeServings={onChangeDefaultServings}
                        onSaveMember={onSaveMember}
                        onSave={async (ms) => { await saveMenuSettings(ms); setSettingsOpen(false); }}
                        onClose={() => setSettingsOpen(false)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

function MealView({ meal, linkedRecipe, memberOf, defaultServings, onAddToShopping, onSaveToVault, t }) {
    const cook = memberOf(meal.assigned_member_id);
    const asstCook = memberOf(meal.assistant_cook_id);
    const dish = memberOf(meal.dishwasher_id);
    const asstDish = memberOf(meal.assistant_dishwasher_id);
    const [showIng, setShowIng] = useState(false);
    const [showSteps, setShowSteps] = useState(false);
    // Ingredients/steps can come from the linked recipe OR from `*_preview` (AI pick not-yet-saved)
    const previewIngredients = meal.ingredients_preview || [];
    const previewSteps = meal.steps_preview || [];
    const ingredients = (linkedRecipe?.ingredients?.length ? linkedRecipe.ingredients : previewIngredients) || [];
    const steps = (linkedRecipe?.steps?.length ? linkedRecipe.steps : previewSteps) || [];
    const baseServings = linkedRecipe?.servings || meal.servings || defaultServings || 4;
    const targetServings = meal.servings || defaultServings;
    const factor = baseServings ? targetServings / baseServings : 1;
    const ingCount = ingredients.length;
    const stepCount = steps.length;
    const isAiPick = !meal.recipe_id && (previewIngredients.length > 0 || previewSteps.length > 0 || meal.recipe_url);
    const isAiOnly = isAiPick && !meal.recipe_url;  // pure LLM-generated (no validated source)
    const countLabel = ingCount === 1
        ? t("week.ingredients_count_one", { n: ingCount })
        : t("week.ingredients_count_other", { n: ingCount });
    const stepLabel = stepCount === 1
        ? t("week.steps_count_one", { n: stepCount })
        : t("week.steps_count_other", { n: stepCount });

    return (
        <div>
            <div className="flex items-start justify-between gap-2">
                <div className="font-display text-xl font-semibold leading-tight tracking-tight">{meal.meal_name}</div>
                {meal.servings && meal.servings !== defaultServings && (
                    <span className="text-[10px] font-bold bg-[#fff4cf] text-[#7a5b00] px-1.5 py-0.5 rounded-md whitespace-nowrap" title={t("week.servings_label")}>
                        {meal.servings}p
                    </span>
                )}
            </div>
            <div className="space-y-1 mt-2">
                {cook && <RolePill label={t("week.role.cook")} m={cook} />}
                {asstCook && <RolePill label={t("week.role.asst_cook")} m={asstCook} />}
                {dish && <RolePill label={t("week.role.dishes")} m={dish} />}
                {asstDish && <RolePill label={t("week.role.asst_dishes")} m={asstDish} />}
            </div>
            {meal.recipe_url ? (
                <a
                    href={meal.recipe_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="meal-recipe-link"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-block mt-2 text-[11px] font-semibold text-[#2F6B43] hover:underline truncate max-w-full"
                >
                    {t("week.open_recipe")}
                </a>
            ) : isAiOnly ? (
                <span
                    className="inline-block mt-2 text-[10px] uppercase tracking-widest font-bold bg-[#fff4cf] text-[#7a5b00] px-1.5 py-0.5 rounded-md"
                    title={t("week.ai_generated_hint")}
                    data-testid={`ai-badge-${meal.id}`}
                >
                    🤖 {t("week.ai_generated_badge")}
                </span>
            ) : null}
            {(meal.notes || meal.recipe) && (
                <div className="mt-2 text-[11px] text-[#5b6d63] line-clamp-3 italic">
                    {meal.notes || meal.recipe}
                </div>
            )}
            {ingCount > 0 && (
                <div className="mt-3 border-t border-[#0d2a1b]/5 pt-2">
                    <button
                        onClick={() => setShowIng((s) => !s)}
                        className="flex items-center gap-1 text-[11px] font-semibold text-[#0d2a1b]/70 hover:text-[#2F6B43]"
                        data-testid={`toggle-ing-${meal.id}`}
                    >
                        {showIng ? <NavArrowUp className="w-3.5 h-3.5" /> : <NavArrowDown className="w-3.5 h-3.5" />}
                        {countLabel}
                    </button>
                    <AnimatePresence>
                        {showIng && (
                            <motion.ul
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden mt-1 space-y-0.5 text-[12px] text-[#0d2a1b]"
                            >
                                {ingredients.map((ing, i) => (
                                    <li key={i} className="flex items-baseline justify-between gap-2 tabular-nums">
                                        <span className="truncate">{ing.name}</span>
                                        <span className="text-[#5b6d63] shrink-0">
                                            {ing.qty != null ? `${formatNum(ing.qty * factor)} ${ing.unit || ""}`.trim() : (ing.unit || "")}
                                        </span>
                                    </li>
                                ))}
                            </motion.ul>
                        )}
                    </AnimatePresence>
                    <button
                        onClick={onAddToShopping}
                        className="mt-2 brut-btn brut-btn-secondary text-[11px] !py-1.5 w-full justify-center"
                        data-testid={`add-meal-to-shopping-${meal.id}`}
                    >
                        <Cart className="w-3.5 h-3.5" /> {t("week.add_to_shopping")}
                    </button>
                </div>
            )}
            {stepCount > 0 && (
                <div className="mt-2">
                    <button
                        onClick={() => setShowSteps((s) => !s)}
                        className="flex items-center gap-1 text-[11px] font-semibold text-[#0d2a1b]/70 hover:text-[#2F6B43]"
                        data-testid={`toggle-steps-${meal.id}`}
                    >
                        {showSteps ? <NavArrowUp className="w-3.5 h-3.5" /> : <NavArrowDown className="w-3.5 h-3.5" />}
                        {stepLabel}
                    </button>
                    <AnimatePresence>
                        {showSteps && (
                            <motion.ol
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden mt-1 space-y-1 text-[12px] text-[#0d2a1b] list-decimal pl-5"
                            >
                                {steps.map((s, i) => (
                                    <li key={i} className="leading-snug">{s}</li>
                                ))}
                            </motion.ol>
                        )}
                    </AnimatePresence>
                </div>
            )}
            {isAiPick && (
                <button
                    onClick={onSaveToVault}
                    className="mt-2 brut-btn brut-btn-secondary text-[11px] !py-1.5 w-full justify-center"
                    data-testid={`save-to-vault-${meal.id}`}
                >
                    <Star className="w-3.5 h-3.5" /> {t("week.save_to_vault_btn")}
                </button>
            )}
            {meal.recipe_id && (
                <div className="mt-2 text-[10px] uppercase tracking-widest font-bold text-[#2F6B43]" data-testid={`in-vault-badge-${meal.id}`}>
                    {t("week.in_vault_badge")}
                </div>
            )}
        </div>
    );
}

function formatNum(n) {
    if (n == null) return "";
    const r = Math.round(n * 100) / 100;
    if (r === Math.floor(r)) return String(Math.floor(r));
    return String(r);
}

function RolePill({ label, m }) {
    return (
        <div className="flex items-center gap-1.5 text-xs text-[#5b6d63]">
            <div
                className="avatar !w-5 !h-5 !text-[9px] !border-white"
                style={{ background: avatarColor(m.name || m.id) }}
            >
                {initials(m.name)}
            </div>
            <span><b className="font-semibold text-[#0d2a1b]">{label}:</b> {firstName(m.name)}</span>
        </div>
    );
}

function MealEditForm({ initial, members, recipes, defaultServings, householdId, onPickRecipe, onCancel, onSave, t }) {
    const [meal_name, setName] = useState(initial?.meal_name || "");
    const [assigned_member_id, setCook] = useState(initial?.assigned_member_id || "");
    const [assistant_cook_id, setAssCook] = useState(initial?.assistant_cook_id || "");
    const [dishwasher_id, setDish] = useState(initial?.dishwasher_id || "");
    const [assistant_dishwasher_id, setAssDish] = useState(initial?.assistant_dishwasher_id || "");
    const [recipe_url, setRecipeUrl] = useState(initial?.recipe_url || "");
    const initialUrl = initial?.recipe_url || "";
    const [notes, setNotes] = useState(initial?.notes || initial?.recipe || "");
    const [saveAsRecipe, setSaveAsRecipe] = useState(!!initial?.recipe_id);
    const [showRoles, setShowRoles] = useState(!!(initial?.assistant_cook_id || initial?.dishwasher_id));
    const [recipe_id, setRecipeId] = useState(initial?.recipe_id || null);
    const [servings, setServings] = useState(initial?.servings || "");
    const [pickingForMe, setPickingForMe] = useState(false);
    // Pick-for-me / fix-menu populate these during the CURRENT form session. We do NOT
    // initialize them from `initial` — that would echo back the existing meal's previews
    // and prevent the backend from re-scraping when the URL changes. Backend already has
    // the old previews in DB; if the URL is unchanged it preserves them automatically.
    const [ingredientsPreview, setIngredientsPreview] = useState(null);
    const [stepsPreview, setStepsPreview] = useState(null);
    const [isAiGenerated, setIsAiGenerated] = useState(null);

    // When the user changes the URL (different from the original), clear carried previews so
    // the backend will scrape the NEW URL on save. Without this, the form echoes back the OLD
    // meal's ingredients/steps and the backend thinks the caller already provided them.
    const onChangeRecipeUrl = (newUrl) => {
        setRecipeUrl(newUrl);
        if ((newUrl || "").trim() !== (initialUrl || "").trim()) {
            setIngredientsPreview(null);
            setStepsPreview(null);
        }
    };

    // "Erase recipe content" — wipe URL + notes + previews + name in one click
    const eraseRecipe = () => {
        if (!window.confirm(t("week.erase_recipe_confirm"))) return;
        setName("");
        setRecipeId(null);
        setRecipeUrl("");
        setNotes("");
        setIngredientsPreview(null);
        setStepsPreview(null);
        setIsAiGenerated(null);
        setSaveAsRecipe(false);
    };

    const handlePick = () => {
        onPickRecipe((r) => {
            setName(r.name);
            setRecipeId(r.id);
            if (r.url) setRecipeUrl(r.url);
            if (r.notes) setNotes(r.notes);
            // keep saveAsRecipe false because the recipe is already in the book
            setSaveAsRecipe(false);
        });
    };

    const handlePickForMe = async () => {
        setPickingForMe(true);
        try {
            const r = await pickOneRecipe(householdId);
            if (!r || r.kind === "none") {
                toast.error(t("week.pick_for_me_failed"));
                return;
            }
            setName(r.name || "");
            setRecipeId(r.recipe_id || null);
            setRecipeUrl(r.url || "");
            setNotes(r.notes || "");
            setSaveAsRecipe(false);
            setIngredientsPreview(r.ingredients_preview || []);
            setStepsPreview(r.steps_preview || []);
            setIsAiGenerated(typeof r.is_ai_generated === "boolean" ? r.is_ai_generated : !r.url);
            toast.success(
                r.kind === "vault"
                    ? t("week.pick_for_me_from_vault", { name: r.name })
                    : t("week.pick_for_me_from_web", { name: r.name })
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setPickingForMe(false);
        }
    };

    const submit = (e) => {
        e.preventDefault();
        if (!meal_name.trim()) return;
        onSave({
            meal_name: meal_name.trim(),
            assigned_member_id: assigned_member_id || null,
            assistant_cook_id: assistant_cook_id || null,
            dishwasher_id: dishwasher_id || null,
            assistant_dishwasher_id: assistant_dishwasher_id || null,
            recipe_url: recipe_url.trim() || null,
            notes: notes.trim() || null,
            save_as_recipe: saveAsRecipe,
            recipe_id: recipe_id || null,
            servings: servings ? parseInt(servings, 10) : null,
            // Only send the preview fields if we actually have data — otherwise undefined
            // and the backend leaves the existing meal values alone.
            ingredients_preview: ingredientsPreview && ingredientsPreview.length ? ingredientsPreview : undefined,
            steps_preview: stepsPreview && stepsPreview.length ? stepsPreview : undefined,
            is_ai_generated: typeof isAiGenerated === "boolean" ? isAiGenerated : undefined,
        });
    };

    return (
        <form onSubmit={submit} className="space-y-2 mt-1">
            <div className="grid grid-cols-2 gap-1.5">
                <button
                    type="button"
                    onClick={handlePick}
                    disabled={!recipes?.length}
                    className="brut-btn brut-btn-secondary !text-[11px] justify-center"
                    data-testid="pick-recipe-btn"
                >
                    {t("week.pick_recipe")}
                </button>
                <button
                    type="button"
                    onClick={handlePickForMe}
                    disabled={pickingForMe}
                    className="brut-btn brut-btn-secondary !text-[11px] justify-center"
                    data-testid="pick-for-me-btn"
                >
                    {pickingForMe ? t("week.pick_for_me_working") : t("week.pick_for_me_btn")}
                </button>
            </div>
            <input
                className="brut-input !py-2 !text-sm"
                placeholder={t("week.placeholder_meal")}
                value={meal_name}
                onChange={(e) => { setName(e.target.value); setRecipeId(null); }}
                autoFocus
                data-testid="meal-name-input"
            />
            <div className="grid grid-cols-2 gap-2">
                <select className="brut-input !py-2 !text-sm" value={assigned_member_id} onChange={(e) => setCook(e.target.value)}>
                    <option value="">{t("week.select.cook")}</option>
                    {members.map((m) => <option key={m.id} value={m.id}>{firstName(m.name)}</option>)}
                </select>
                <select className="brut-input !py-2 !text-sm" value={dishwasher_id} onChange={(e) => setDish(e.target.value)}>
                    <option value="">{t("week.select.dishes")}</option>
                    {members.map((m) => <option key={m.id} value={m.id}>{firstName(m.name)}</option>)}
                </select>
            </div>
            {showRoles && (
                <div className="grid grid-cols-2 gap-2">
                    <select className="brut-input !py-2 !text-sm" value={assistant_cook_id} onChange={(e) => setAssCook(e.target.value)}>
                        <option value="">{t("week.select.asst_cook")}</option>
                        {members.map((m) => <option key={m.id} value={m.id}>{firstName(m.name)}</option>)}
                    </select>
                    <select className="brut-input !py-2 !text-sm" value={assistant_dishwasher_id} onChange={(e) => setAssDish(e.target.value)}>
                        <option value="">{t("week.select.asst_dishes")}</option>
                        {members.map((m) => <option key={m.id} value={m.id}>{firstName(m.name)}</option>)}
                    </select>
                </div>
            )}
            {!showRoles && (
                <button type="button" onClick={() => setShowRoles(true)} className="text-[11px] font-semibold text-[#2F6B43] hover:underline">
                    {t("week.add_assistants")}
                </button>
            )}
            <div className="flex items-center gap-2">
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#5b6d63] mb-0.5">
                        {t("week.servings_label")}
                    </div>
                    <input
                        type="number"
                        min="1"
                        max="50"
                        value={servings}
                        onChange={(e) => setServings(e.target.value)}
                        placeholder={t("week.servings_default_hint", { n: defaultServings })}
                        className="brut-input !py-2 !text-sm w-full"
                        data-testid="meal-servings-input"
                    />
                </div>
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#5b6d63] mb-0.5">
                    {t("week.recipe_url_label")}
                </div>
                <input
                    type="url"
                    className="brut-input !py-2 !text-sm"
                    placeholder="https://…"
                    value={recipe_url}
                    onChange={(e) => onChangeRecipeUrl(e.target.value)}
                    data-testid="meal-url-input"
                />
            </div>
            <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#5b6d63] mb-0.5">
                    {t("week.notes_label")}
                </div>
                <textarea
                    className="brut-input !py-2 !text-sm min-h-[60px]"
                    placeholder={t("week.recipe_placeholder")}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="meal-notes-input"
                />
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer select-none">
                <input
                    type="checkbox"
                    checked={saveAsRecipe}
                    onChange={(e) => setSaveAsRecipe(e.target.checked)}
                    className="w-4 h-4 accent-[#2F6B43]"
                    data-testid="meal-save-as-recipe"
                />
                <span>📖 {t("week.save_as_recipe")}</span>
            </label>
            <div className="flex gap-2 pt-1">
                <button type="submit" className="brut-btn text-xs flex-1 justify-center" data-testid="save-meal-btn">
                    <Check className="w-3.5 h-3.5" /> {t("common.save")}
                </button>
                <button type="button" onClick={onCancel} className="brut-btn brut-btn-secondary text-xs" aria-label={t("common.cancel")}>
                    <Xmark className="w-3.5 h-3.5" />
                </button>
                {!!(initial?.meal_name || initial?.recipe_url || initial?.recipe_id) && (
                    <button
                        type="button"
                        onClick={eraseRecipe}
                        className="brut-btn brut-btn-secondary text-xs !text-[#A53F3F] hover:!bg-[#fbe0dc]"
                        aria-label={t("week.erase_recipe_btn")}
                        title={t("week.erase_recipe_btn")}
                        data-testid="erase-recipe-btn"
                    >
                        <Trash className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </form>
    );
}

function RecipePickerModal({ recipes, onPick, onClose, t }) {
    const [q, setQ] = useState("");
    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return recipes;
        return recipes.filter((r) =>
            (r.name || "").toLowerCase().includes(s) ||
            (r.notes || "").toLowerCase().includes(s)
        );
    }, [recipes, q]);
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={onClose}
            data-testid="recipe-picker-modal"
        >
            <motion.div
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 12, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#FBF6EB] w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl brut-border brut-shadow max-h-[85vh] flex flex-col"
            >
                <div className="p-4 border-b-2 border-[#0D2A1B] flex items-center justify-between">
                    <div className="font-display text-xl font-bold">{t("week.picker_title")}</div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-white hover:bg-[#faf5ea] flex items-center justify-center" data-testid="picker-close-btn">
                        <Xmark className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-3 border-b border-[#0d2a1b]/10">
                    <div className="relative">
                        <Search className="w-4 h-4 text-[#5b6d63] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={t("week.picker_search")}
                            className="brut-input !pl-9 !py-2 text-sm w-full"
                            data-testid="picker-search-input"
                        />
                    </div>
                </div>
                <div className="overflow-y-auto p-3 flex-1">
                    {filtered.length === 0 ? (
                        <div className="text-center text-sm text-[#5b6d63] py-8">{t("week.picker_empty")}</div>
                    ) : (
                        <ul className="space-y-2">
                            {filtered.map((r) => (
                                <li key={r.id}>
                                    <button
                                        onClick={() => onPick(r)}
                                        className="w-full text-left brut-card p-3 hover:bg-[#f5f0e0] transition-colors"
                                        data-testid={`picker-item-${r.id}`}
                                    >
                                        <div className="font-display text-base font-bold tracking-tight">{r.name}</div>
                                        <div className="text-[11px] text-[#5b6d63] mt-0.5 flex items-center gap-2 flex-wrap">
                                            {r.servings && <span>{r.servings} {t("recipes.servings_unit")}</span>}
                                            {r.ingredients?.length > 0 && <span>· {r.ingredients.length} {r.ingredients.length === 1 ? t("week.ingredients_count_one", { n: r.ingredients.length }).split(" ").slice(1).join(" ") : t("week.ingredients_count_other", { n: r.ingredients.length }).split(" ").slice(1).join(" ")}</span>}
                                            {r.avg_rating != null && <span>· ★ {r.avg_rating.toFixed(1)}</span>}
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
}


const PRIORITY_KEYS = ["uses_expiring", "high_rated", "avoid_recent", "uses_inventory", "balance_cooks"];
const ROLE_TOGGLES = ["auto_assign_cook", "auto_assign_asst_cook", "auto_assign_dishwasher", "auto_assign_asst_dishwasher"];

function MenuSettingsModal({ initial, defaultServings, members, isAdmin, overwrite, setOverwrite, onChangeServings, onSaveMember, onSave, onClose, t }) {
    const init = initial || {};
    const [vaultRatio, setVaultRatio] = useState(
        typeof init.vault_ratio === "number" ? init.vault_ratio : 0.5
    );
    const [weights, setWeights] = useState({
        uses_expiring: 5, high_rated: 4, avoid_recent: 3, uses_inventory: 3, balance_cooks: 2,
        ...(init.priority_weights || {}),
    });
    const [autoCheck, setAutoCheck] = useState(!!init.auto_check_in_stock);
    const [roleToggles, setRoleToggles] = useState({
        auto_assign_cook: init.auto_assign_cook !== false,
        auto_assign_asst_cook: init.auto_assign_asst_cook !== false,
        auto_assign_dishwasher: init.auto_assign_dishwasher !== false,
        auto_assign_asst_dishwasher: init.auto_assign_asst_dishwasher !== false,
    });
    const [mustDiffer, setMustDiffer] = useState(init.cook_dish_must_differ !== false);
    const [scrapeUrls, setScrapeUrls] = useState(init.scrape_recipe_urls !== false);
    const [allowNoUrl, setAllowNoUrl] = useState(init.allow_recipes_without_url === true);
    const [favoriteSite, setFavoriteSite] = useState(typeof init.favorite_cooking_site === "string" ? init.favorite_cooking_site : "");
    // Ranked list of favorite cooking sites. Falls back to the legacy single-string field
    // when present (one-time migration on first edit).
    const [favoriteSites, setFavoriteSites] = useState(() => {
        if (Array.isArray(init.favorite_cooking_sites) && init.favorite_cooking_sites.length) {
            return init.favorite_cooking_sites.filter((s) => typeof s === "string" && s.trim());
        }
        if (typeof init.favorite_cooking_site === "string" && init.favorite_cooking_site.trim()) {
            return [init.favorite_cooking_site.trim()];
        }
        return [];
    });
    const [newSiteDraft, setNewSiteDraft] = useState("");
    const [servings, setServings] = useState(defaultServings || 4);
    const vaultPct = Math.round(vaultRatio * 100);
    const webPct = 100 - vaultPct;

    const submit = async () => {
        // Persist portions immediately via the dedicated handler (any member can do this)
        if (servings && servings !== defaultServings) {
            await onChangeServings(servings);
        }
        // menu_settings PATCH is admin-only; skip the call for non-admins
        if (isAdmin) {
            await onSave({
                vault_ratio: vaultRatio,
                priority_weights: weights,
                auto_check_in_stock: autoCheck,
                cook_dish_must_differ: mustDiffer,
                scrape_recipe_urls: scrapeUrls,
                allow_recipes_without_url: allowNoUrl,
                favorite_cooking_site: (favoriteSite || "").trim(),
                favorite_cooking_sites: favoriteSites,
                ...roleToggles,
            });
        } else {
            onClose();
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
            data-testid="menu-settings-modal"
        >
            <motion.div
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 12, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#FBF6EB] w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl brut-border brut-shadow max-h-[90vh] flex flex-col"
            >
                <div className="p-4 border-b-2 border-[#0D2A1B] flex items-center justify-between">
                    <div className="font-display text-xl font-bold">{t("week.hh_settings_title")}</div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full bg-white hover:bg-[#faf5ea] flex items-center justify-center"
                        data-testid="menu-settings-close"
                        aria-label="close"
                    >
                        <Xmark className="w-4 h-4" />
                    </button>
                </div>
                <div className="overflow-y-auto p-4 space-y-6 flex-1">

                    {/* Portions */}
                    <Section title={t("week.hh_servings_label")} hint={t("week.hh_servings_help")}>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setServings((s) => Math.max(1, s - 1))}
                                className="brut-btn brut-btn-secondary !px-3 !py-1.5 text-sm"
                                data-testid="hh-servings-minus"
                                aria-label="-1"
                            >−</button>
                            <input
                                type="number"
                                min="1"
                                max="50"
                                value={servings}
                                onChange={(e) => setServings(parseInt(e.target.value, 10) || 1)}
                                className="brut-input !py-1.5 !text-base !w-16 text-center font-bold"
                                data-testid="hh-servings-input"
                            />
                            <button
                                onClick={() => setServings((s) => Math.min(50, s + 1))}
                                className="brut-btn brut-btn-secondary !px-3 !py-1.5 text-sm"
                                data-testid="hh-servings-plus"
                                aria-label="+1"
                            >+</button>
                        </div>
                    </Section>

                    {/* Fix-my-menu admin-only settings */}
                    {isAdmin && (
                        <>
                            <Section title={t("week.fix_vault_ratio_label")}>
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="10"
                                    value={vaultPct}
                                    onChange={(e) => setVaultRatio(parseInt(e.target.value, 10) / 100)}
                                    className="w-full accent-[#2F6B43]"
                                    data-testid="vault-ratio-slider"
                                />
                                <div className="text-[12px] text-[#5b6d63] mt-1 text-center font-semibold">
                                    {t("week.fix_vault_ratio_hint", { vault: vaultPct, web: webPct })}
                                </div>
                            </Section>

                            <Section title={t("week.fix_priorities_label")} hint={t("week.fix_priorities_hint")}>
                                <ul className="space-y-2">
                                    {PRIORITY_KEYS.map((k) => (
                                        <li key={k} className="flex items-center justify-between gap-3">
                                            <span className="text-sm text-[#0d2a1b] flex-1 min-w-0">{t(`week.fix_priority.${k}`)}</span>
                                            <input
                                                type="number"
                                                min="0"
                                                max="10"
                                                value={weights[k] ?? 0}
                                                onChange={(e) => setWeights((w) => ({ ...w, [k]: parseInt(e.target.value, 10) || 0 }))}
                                                className="brut-input !py-1.5 !text-sm !w-16 text-center font-bold shrink-0"
                                                data-testid={`weight-${k}`}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </Section>

                            <Section title={t("week.roles_section_title")} hint={t("week.roles_section_hint")}>
                                <ul className="space-y-2">
                                    {ROLE_TOGGLES.map((k) => (
                                        <Toggle
                                            key={k}
                                            id={k}
                                            label={t(`week.role_toggle.${k}`)}
                                            checked={roleToggles[k]}
                                            onChange={(v) => setRoleToggles((s) => ({ ...s, [k]: v }))}
                                            testId={`role-toggle-${k}`}
                                        />
                                    ))}
                                    <Toggle
                                        id="cook_dish_must_differ"
                                        label={t("week.role_toggle.cook_dish_must_differ")}
                                        hint={t("week.role_toggle.cook_dish_must_differ_hint")}
                                        checked={mustDiffer}
                                        onChange={setMustDiffer}
                                        testId="role-toggle-must-differ"
                                    />
                                </ul>
                            </Section>

                            <Section title={t("week.family_section_title")} hint={t("week.family_section_hint")}>
                                <ul className="space-y-2">
                                    {(members || []).map((m) => (
                                        <FamilyMemberRow key={m.id} m={m} onSave={onSaveMember} t={t} />
                                    ))}
                                </ul>
                            </Section>

                            <Section title={t("week.scrape_title")} hint={t("week.scrape_hint")}>
                                <div className="space-y-3">
                                    <div className="space-y-1.5">
                                        <label className="block text-[11px] uppercase tracking-widest font-bold text-[#5b6d63]">
                                            {t("week.favorite_sites_label")}
                                        </label>
                                        <p className="text-[11px] text-[#5b6d63] leading-snug">{t("week.favorite_sites_hint")}</p>
                                        {favoriteSites.length > 0 && (
                                            <ul className="space-y-1.5" data-testid="favorite-sites-list">
                                                {favoriteSites.map((s, idx) => (
                                                    <li key={`${s}-${idx}`} className="flex items-center gap-1.5 bg-white border border-[#0d2a1b]/10 rounded-md px-2 py-1.5">
                                                        <span className="text-[11px] font-bold text-[#2F6B43] tabular-nums shrink-0">{idx + 1}.</span>
                                                        <span className="flex-1 min-w-0 text-xs truncate" title={s}>{s}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => idx > 0 && setFavoriteSites((arr) => {
                                                                const next = [...arr];
                                                                [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                                                                return next;
                                                            })}
                                                            disabled={idx === 0}
                                                            className="w-7 h-7 rounded-full bg-[#faf5ea] hover:bg-[#e8f1e4] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                                                            aria-label="move up"
                                                            data-testid={`fav-site-up-${idx}`}
                                                        >
                                                            <NavArrowUp className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => idx < favoriteSites.length - 1 && setFavoriteSites((arr) => {
                                                                const next = [...arr];
                                                                [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                                                                return next;
                                                            })}
                                                            disabled={idx === favoriteSites.length - 1}
                                                            className="w-7 h-7 rounded-full bg-[#faf5ea] hover:bg-[#e8f1e4] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                                                            aria-label="move down"
                                                            data-testid={`fav-site-down-${idx}`}
                                                        >
                                                            <NavArrowDown className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setFavoriteSites((arr) => arr.filter((_, i) => i !== idx))}
                                                            className="w-7 h-7 rounded-full bg-[#fbe0dc] hover:bg-[#f9c8c0] flex items-center justify-center shrink-0"
                                                            aria-label="remove"
                                                            data-testid={`fav-site-remove-${idx}`}
                                                        >
                                                            <Xmark className="w-3.5 h-3.5" />
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        <div className="flex gap-1.5">
                                            <input
                                                type="url"
                                                inputMode="url"
                                                value={newSiteDraft}
                                                onChange={(e) => setNewSiteDraft(e.target.value)}
                                                placeholder={t("week.favorite_site_placeholder")}
                                                className="brut-input !py-2 !text-sm flex-1 min-w-0"
                                                data-testid="favorite-site-input"
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        const s = newSiteDraft.trim();
                                                        if (s && (s.startsWith("http://") || s.startsWith("https://")) && favoriteSites.length < 10) {
                                                            setFavoriteSites((arr) => arr.includes(s) ? arr : [...arr, s]);
                                                            setNewSiteDraft("");
                                                        }
                                                    }
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const s = newSiteDraft.trim();
                                                    if (!s) return;
                                                    if (!(s.startsWith("http://") || s.startsWith("https://"))) {
                                                        toast.error(t("week.favorite_site_invalid"));
                                                        return;
                                                    }
                                                    if (favoriteSites.length >= 10) {
                                                        toast.error("Max 10 sites");
                                                        return;
                                                    }
                                                    if (favoriteSites.includes(s)) return;
                                                    setFavoriteSites((arr) => [...arr, s]);
                                                    setNewSiteDraft("");
                                                }}
                                                disabled={!newSiteDraft.trim()}
                                                className="brut-btn text-xs px-3 disabled:opacity-50"
                                                data-testid="favorite-site-add-btn"
                                            >
                                                <Plus className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    <Toggle
                                        id="scrape_recipe_urls"
                                        label={t("week.scrape_toggle")}
                                        checked={scrapeUrls}
                                        onChange={setScrapeUrls}
                                        testId="scrape-toggle"
                                    />
                                    <Toggle
                                        id="allow_recipes_without_url"
                                        label={t("week.allow_no_url_toggle")}
                                        hint={t("week.allow_no_url_hint")}
                                        checked={allowNoUrl}
                                        onChange={setAllowNoUrl}
                                        testId="allow-no-url-toggle"
                                    />
                                </div>
                            </Section>

                            <Section title={t("shop.fix_btn")}>
                                <Toggle
                                    id="auto_check_in_stock"
                                    label={t("week.fix_auto_check_label")}
                                    hint={t("week.fix_auto_check_hint")}
                                    checked={autoCheck}
                                    onChange={setAutoCheck}
                                    testId="auto-check-toggle"
                                />
                            </Section>

                            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer select-none border-t border-[#0d2a1b]/10 pt-3">
                                <input
                                    type="checkbox"
                                    checked={overwrite}
                                    onChange={(e) => setOverwrite(e.target.checked)}
                                    className="w-4 h-4 accent-[#A53F3F]"
                                    data-testid="overwrite-checkbox"
                                />
                                <span>{t("week.fix_overwrite_label")}</span>
                            </label>
                        </>
                    )}
                </div>
                <div className="p-3 border-t border-[#0d2a1b]/10 flex gap-2">
                    <button
                        onClick={submit}
                        className="brut-btn text-sm flex-1 justify-center"
                        data-testid="save-menu-settings-btn"
                    >
                        <Check className="w-4 h-4" /> {t("common.save")}
                    </button>
                    <button
                        onClick={onClose}
                        className="brut-btn brut-btn-secondary text-sm"
                        data-testid="cancel-menu-settings-btn"
                    >
                        <Xmark className="w-4 h-4" />
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}

function Section({ title, hint, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#5b6d63] mb-1">{title}</div>
            {hint && <div className="text-[11px] text-[#5b6d63] mb-2 leading-snug">{hint}</div>}
            {children}
        </div>
    );
}

function Toggle({ id, label, hint, checked, onChange, testId }) {
    return (
        <label className="flex items-start gap-2 text-sm font-semibold cursor-pointer select-none">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="w-4 h-4 accent-[#2F6B43] mt-0.5"
                data-testid={testId}
                id={id}
            />
            <span className="min-w-0">
                {label}
                {hint && <span className="block text-[11px] font-normal text-[#5b6d63] mt-0.5">{hint}</span>}
            </span>
        </label>
    );
}

function FamilyMemberRow({ m, onSave, t }) {
    const caps = m.capabilities || { can_cook: true, can_do_dishes: true };
    const [canCook, setCanCook] = useState(!!caps.can_cook);
    const [canDish, setCanDish] = useState(!!caps.can_do_dishes);
    const dirty = canCook !== !!caps.can_cook || canDish !== !!caps.can_do_dishes;
    const save = () => onSave(m.id, { capabilities: { can_cook: canCook, can_do_dishes: canDish } });
    return (
        <li className="flex items-center justify-between gap-3 flex-wrap" data-testid={`member-row-${m.id}`}>
            <div className="font-semibold text-sm min-w-0 flex-1 truncate">{m.name}</div>
            <div className="flex items-center gap-3 text-[11px]">
                <label className="flex items-center gap-1 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={canCook}
                        onChange={(e) => setCanCook(e.target.checked)}
                        className="w-3.5 h-3.5 accent-[#2F6B43]"
                        data-testid={`member-can-cook-${m.id}`}
                    />
                    {t("week.member.can_cook")}
                </label>
                <label className="flex items-center gap-1 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={canDish}
                        onChange={(e) => setCanDish(e.target.checked)}
                        className="w-3.5 h-3.5 accent-[#2F6B43]"
                        data-testid={`member-can-dish-${m.id}`}
                    />
                    {t("week.member.can_dish")}
                </label>
                {dirty && (
                    <button
                        type="button"
                        onClick={save}
                        className="brut-btn !py-1 !px-2 text-[10px]"
                        data-testid={`member-save-${m.id}`}
                    >
                        <Check className="w-3 h-3" />
                    </button>
                )}
            </div>
        </li>
    );
}
